import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// --- Calendar Pane ---
const CalendarPane = ({
  value,
  onChange
}: {
  value: Date | null,
  onChange: (d: Date) => void
}) => {
  const [viewDate, setViewDate] = useState(value || new Date());

  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const firstDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: firstDay }, (_, i) => i);

  return (
    <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl select-none min-w-[250px]">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
          <ChevronLeft size={16} className="text-slate-600 dark:text-slate-300" />
        </button>
        <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
          {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </span>
        <button type="button" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
          <ChevronRight size={16} className="text-slate-600 dark:text-slate-300" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i} className="text-[10px] font-bold text-slate-400">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {blanks.map(i => <div key={`b-${i}`} />)}
        {days.map(d => {
          const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), d);
          const isSelected = value &&
            date.getFullYear() === value.getFullYear() &&
            date.getMonth() === value.getMonth() &&
            date.getDate() === value.getDate();
          const isToday = date.toDateString() === new Date().toDateString();

          return (
            <button
              key={d}
              type="button"
              onClick={() => onChange(date)}
              className={`h-8 w-8 rounded-lg text-xs font-medium transition-all
                ${isSelected ? 'bg-primary text-white shadow-md' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'}
                ${isToday && !isSelected ? 'text-primary font-bold bg-primary/10 dark:bg-primary/20' : ''}
              `}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- Helpers ---
const toIso = (m: string, d: string, y: string): string | null => {
  const pm = m.padStart(2, '0');
  const pd = d.padStart(2, '0');
  const fy = y.length === 2 ? `20${y}` : y;

  const im = parseInt(pm), id = parseInt(pd), iy = parseInt(fy);
  if (!iy || !im || !id) return null;

  const test = new Date(iy, im - 1, id);
  if (test.getFullYear() !== iy || test.getMonth() !== im - 1 || test.getDate() !== id) return null;

  return `${fy}-${pm}-${pd}`;
};

// --- Main Component ---
// value/onChange use YYYY-MM-DD format, display is MM/DD/YYYY
export const ProDatePicker = ({
  value,
  onChange,
  className = "",
  title,
}: {
  value: string | null,
  onChange: (val: string) => void,
  className?: string,
  title?: string,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const [mBuf, setMBuf] = useState('');
  const [dBuf, setDBuf] = useState('');
  const [yBuf, setYBuf] = useState('');

  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Sync buffers from external value (YYYY-MM-DD)
  useEffect(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      setMBuf(m); setDBuf(d); setYBuf(y);
    } else if (!value) {
      setMBuf(''); setDBuf(''); setYBuf('');
    }
  }, [value]);

  const flush = () => {
    if (!mBuf && !dBuf && !yBuf) {
      if (value) onChange('');
      return;
    }
    if (!mBuf || !dBuf || !yBuf) return;
    const iso = toIso(mBuf, dBuf, yBuf);
    if (iso) {
      const [y, m, d] = iso.split('-');
      setMBuf(m); setDBuf(d); setYBuf(y);
      onChange(iso);
    } else if (value) {
      // Revert to last good value
      const [y, m, d] = value.split('-');
      setMBuf(m); setDBuf(d); setYBuf(y);
    }
  };

  const handleInput = (segment: 'm' | 'd' | 'y', raw: string) => {
    const val = raw.replace(/\D/g, '');
    if (segment === 'm') {
      const v = val.slice(0, 2);
      setMBuf(v);
      if (v.length === 2) dayRef.current?.focus();
      else if (v.length === 1 && parseInt(v) > 1) {
        setMBuf(`0${v}`);
        dayRef.current?.focus();
      }
    } else if (segment === 'd') {
      const v = val.slice(0, 2);
      setDBuf(v);
      if (v.length === 2) yearRef.current?.focus();
      else if (v.length === 1 && parseInt(v) > 3) {
        setDBuf(`0${v}`);
        yearRef.current?.focus();
      }
    } else {
      setYBuf(val.slice(0, 4));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, segment: 'm' | 'd' | 'y') => {
    if (e.key === 'Backspace') {
      const atStart = e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 0;
      if (atStart) {
        e.preventDefault();
        if (segment === 'd') monthRef.current?.focus();
        if (segment === 'y') dayRef.current?.focus();
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      flush();
      setIsOpen(false);
      e.currentTarget.blur();
    }
    if (e.key === 'Tab' && !e.shiftKey && segment === 'y') {
      flush();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen(true);
  };

  const handleCalendarSelect = (date: Date) => {
    const y = String(date.getFullYear());
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    onChange(`${y}-${m}-${d}`);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget) && !calendarRef.current?.contains(e.relatedTarget as Node)) {
          flush();
          setIsOpen(false);
        }
      }}
    >
      <div title={title} className={`flex items-center gap-1.5 px-2.5 h-8 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-700 focus-within:border-primary focus-within:ring-primary transition-all ${className}`}>
        <div className="flex items-center flex-1 text-sm font-medium text-slate-900 dark:text-white">
          <input
            ref={monthRef}
            placeholder="MM"
            value={mBuf}
            onFocus={handleFocus}
            onChange={(e) => handleInput('m', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'm')}
            className="w-6 bg-transparent outline-none text-center placeholder:text-slate-300"
          />
          <span className="text-slate-300">/</span>
          <input
            ref={dayRef}
            placeholder="DD"
            value={dBuf}
            onFocus={handleFocus}
            onChange={(e) => handleInput('d', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'd')}
            className="w-6 bg-transparent outline-none text-center placeholder:text-slate-300"
          />
          <span className="text-slate-300">/</span>
          <input
            ref={yearRef}
            placeholder="YYYY"
            value={yBuf}
            onFocus={handleFocus}
            onChange={(e) => handleInput('y', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'y')}
            className="w-10 bg-transparent outline-none text-center placeholder:text-slate-300"
          />
        </div>
      </div>

      {isOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => { flush(); setIsOpen(false); }} />
          <div ref={calendarRef} className="fixed z-[9999]" style={{ top: coords.top, left: coords.left }}>
            <CalendarPane
              value={value ? new Date(value + 'T00:00:00') : null}
              onChange={handleCalendarSelect}
            />
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
